import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useEffect, useState } from 'react';
import { RegisterPaymentList } from './RegisterPaymentList/RegisterPaymentList';
import { registerPaidyTokenApi } from '@/api/payment';
import { isApp } from '@/config/device';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { useCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useGmo } from '@/hooks/useGmo';
import { usePaidy } from '@/hooks/usePaidy';
import { Car } from '@/types/api/customerApi';
import { persistLoginContext } from '@/utils/loginTel';
import { f7CustomBack } from '@/utils/utils';

type RegisterPaymentListPageProps = {
  redirectPage: string;
  f7router: Router.Router;
  canWithdrawFromBank: boolean;
  currentCarId?: string;
};

export const RegisterPaymentListPage: FC<RegisterPaymentListPageProps> = ({
  redirectPage = 'prepaid',
  f7router,
  currentCarId,
  canWithdrawFromBank,
}) => {
  const { m_car_id, t_order_id } =
    (useStore(store, 'carItemInfo') as Car) || {};
  const [actionUrl, setActionUrl] = useState('');
  const mPrepaidId = window.localStorage.getItem('m_prepaid_id');
  const returnUrl = paths.prepaidSettingStatus('gmo_success');
  const cancelUrl = paths.prepaidSettingStatus('gmo_cancel');
  const { addCreditCardPaymentMethod, GMOCardEditParameters } =
    useGmo(redirectPage);
  const { launchPaidyCheckout } = usePaidy();
  const { setCustomerInfo } = useCustomerInfo({
    m_customer_id: store.state.authInfo.m_customer_id,
  });

  const handleClickPaidyPayment = async () => {
    try {
      const result = await launchPaidyCheckout({ mode: 'token' });
      if (result.success && result.tokenId) {
        f7.preloader.show();
        const customerInfo = store.state.customerInfo;
        const res = await registerPaidyTokenApi({
          m_customer_id: store.state.authInfo.m_customer_id,
          paidy_token_id: result.tokenId,
          customer_name: `${customerInfo?.family_name ?? ''} ${
            customerInfo?.first_name ?? ''
          }`.trim(),
          customer_email: customerInfo?.email ?? '',
          phone_number: customerInfo?.phone_no1 ?? '',
        });
        f7.preloader.hide();
        if (res.data.success) {
          setCustomerInfo();
          f7.dialog.alert('', 'あと払い（ペイディ）の登録が完了しました');
          f7CustomBack(['#RegisterPaymentList']);
        } else {
          f7.dialog.alert('', 'あと払い（ペイディ）の登録に失敗しました');
        }
      }
    } catch (error) {
      console.error('Paidy registration error:', error);
      f7.preloader.hide();
      f7.dialog.alert('', 'エラーが発生しました');
    }
  };

  const handleClickWithdrawFromBank = () => {
    const { globalTheme, isLoggedInToMaikuru, paymentMethodsInfo } =
      store.state;
    const payments = paymentMethodsInfo.map(
      ({ m_payment_method_id, m_payment_type_id }) => ({
        m_payment_method_id,
        m_payment_type_id,
      }),
    );
    window.localStorage.setItem('payment_methods', JSON.stringify(payments));
    window.localStorage.setItem('m_car_id', String(m_car_id));
    persistLoginContext(store.state.loginInfo);
    window.localStorage.setItem('t_order_id', String(t_order_id));
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    window.localStorage.setItem('source_page', paths.chargePrepaid);
    // GMOリダイレクト後にprepaidNameが失われるため、localStorageに保存
    const prepaidName = store.state.advancePaymentsInfo?.[0]?.name;
    if (prepaidName) {
      window.localStorage.setItem('prepaidName', prepaidName);
    }
    f7.view.main.router.navigate(paths.registerBank, {
      props: {
        redirectPage: redirectPage,
      },
    });
  };

  const handleClickCreditCardPayment = async () => {
    const { token, m_customer_id } = store.state.authInfo;
    const { globalTheme, isLoggedInToMaikuru, paymentMethodsInfo } =
      store.state;
    const payments = paymentMethodsInfo.map(
      ({ m_payment_method_id, m_payment_type_id }) => ({
        m_payment_method_id,
        m_payment_type_id,
      }),
    );
    window.localStorage.setItem('payment_methods', JSON.stringify(payments));
    window.localStorage.setItem('token', token);
    window.localStorage.setItem('m_customer_id', m_customer_id.toString());
    window.localStorage.setItem('m_car_id', String(m_car_id));
    window.localStorage.setItem('t_order_id', String(t_order_id));
    persistLoginContext(store.state.loginInfo);
    currentCarId && window.localStorage.setItem('currentCarId', currentCarId);
    window.localStorage.setItem('globalTheme', `${globalTheme}`);
    window.localStorage.setItem(
      'isLoggedInToMaikuru',
      `${isLoggedInToMaikuru}`,
    );
    window.localStorage.setItem('destination_step', '2');
    window.localStorage.setItem('destination_page', paths.reservings);
    window.localStorage.setItem('source_page', paths.chargePrepaid);
    // GMOリダイレクト後にprepaidNameが失われるため、localStorageに保存
    const prepaidName = store.state.advancePaymentsInfo?.[0]?.name;
    if (prepaidName) {
      window.localStorage.setItem('prepaidName', prepaidName);
    }
    // 一回目のクリック時点ではsetActionUrlでactionUrlが更新されるが
    // 非同期処理のためから文字のまま
    // document.getElementById('submit')?.click();
    try {
      const response = await addCreditCardPaymentMethod(returnUrl, cancelUrl);
      if (!response) {
        f7.dialog.alert('', `エラーが発生しました`);
        return;
      }
      if (
        response.error ||
        !response.success ||
        !('LinkUrl' in response.data)
      ) {
        f7.dialog.alert('', `エラーが発生しました`);
      } else {
        const GMOLinkTypePlusUrl = response.data.LinkUrl;
        if (isApp) {
          location.href = GMOLinkTypePlusUrl;
        } else {
          window.location.href = GMOLinkTypePlusUrl;
        }
      }
    } catch (error) {
      console.error('GMOパラメータの生成に失敗しました', error);
      f7.dialog.alert('', `エラーが発生しました`);
    } finally {
      // setIsProcessing(false);
    }
  };

  const handleClickPopupClose = () => {
    f7CustomBack(['#RegisterPaymentList']);
  };

  // 一回目のクリックでactionUrlが更新されたらもう一度クリック
  useEffect(() => {
    document.getElementById('submit')?.click();
  }, [actionUrl]);

  return (
    <RegisterPaymentList
      handleClickWithdrawFromBank={handleClickWithdrawFromBank}
      handleClickCreditCardPayment={handleClickCreditCardPayment}
      handleClickPaidyPayment={handleClickPaidyPayment}
      GMOCardEditParameters={GMOCardEditParameters}
      actionUrl={actionUrl}
      redirectPage={redirectPage}
      canWithdrawFromBank={canWithdrawFromBank}
      handleClickPopupClose={handleClickPopupClose}
    />
  );
};
